/**
 * Tiny resilient fetch helper for product/catalog endpoints.
 *
 * Why this exists: customers reported the products grid showing "No products
 * found" / "Trouble loading products" right after they logged in or whenever
 * the backend was redeploying. Root cause was a transient 5xx / network blip
 * that left `products = []` with no recovery path.
 *
 * Retry strategy (Feb 2026 update):
 *   - 4 attempts total (initial + 3 retries) with exponential backoff:
 *     immediate, 300ms, 900ms, 2700ms ⇒ ~3.9s of patience before giving up.
 *     This covers the 2-5s cold-start window after a typical deploy.
 *   - Each attempt has an 8s AbortController timeout so a hung fetch can't
 *     leave the UI stuck on the loading state forever.
 *   - Retries on transient 5xx, 429, network errors AND fetch timeouts.
 *
 * Returns { ok, data, error } — never throws.
 */
const PER_ATTEMPT_TIMEOUT_MS = 8000;

export async function resilientFetch(url, options = {}, { retries = 3, baseDelay = 300 } = {}) {
  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), PER_ATTEMPT_TIMEOUT_MS);
    try {
      const res = await fetch(url, { ...options, signal: ac.signal });
      clearTimeout(timer);
      // Retry on transient 5xx / 429
      if (res.status >= 500 || res.status === 429) {
        lastError = new Error(`HTTP ${res.status}`);
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, baseDelay * Math.pow(3, attempt)));
          continue;
        }
        return { ok: false, status: res.status, error: lastError };
      }
      // Non-retriable status (200, 401, 403, 404 etc.) — caller decides
      if (!res.ok) {
        return { ok: false, status: res.status, error: new Error(`HTTP ${res.status}`) };
      }
      let data;
      try {
        data = await res.json();
      } catch (e) {
        return { ok: false, status: res.status, error: new Error('Invalid JSON response') };
      }
      return { ok: true, status: res.status, data };
    } catch (e) {
      clearTimeout(timer);
      lastError = e;
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, baseDelay * Math.pow(3, attempt)));
        continue;
      }
      return { ok: false, error: lastError || new Error('Network error') };
    }
  }
  return { ok: false, error: lastError || new Error('Unknown error') };
}
